import React, { useEffect, useRef, useState } from 'react'

import { Flex, Grid, GridItem, Link, Modal, ModalContent, ModalOverlay, Switch, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useAcceptCookiesMutation } from '@core/app/api/authApi'
import { IconCookie } from '@core/assets/icons/cookie'
import { AppButton } from '@core/components/AppButton'

const baseClass = 'CookieConsent'

const CookieConsentModal: React.FC = () => {
    const [acceptCookies, { isLoading, isSuccess, isError, data, status }] = useAcceptCookiesMutation()
    const { t } = useTranslation()
    const location = useLocation()
    const [showModal, setShowModal] = useState(false)
    const [necessary, setNecessary] = useState(true)
    const [statistics, setStatistics] = useState(false)
    const [marketing, setMarketing] = useState(false)
    const [cookiesData, setCookiesData] = useState(extractCookiesData())

    const scrollRef = useRef<HTMLDivElement>(null)
    const [showTopShadow, setShowTopShadow] = useState(false)
    const [showBottomShadow, setShowBottomShadow] = useState(false)

    const handleScroll = () => {
        if (!scrollRef.current) return

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
        setShowTopShadow(scrollTop > 0)
        setShowBottomShadow(scrollTop + clientHeight < scrollHeight)
    }

    useEffect(() => {
        if (!showModal) return
        const current = scrollRef.current
        if (!current) return

        handleScroll()
        current.addEventListener('scroll', handleScroll)

        return () => {
            current.removeEventListener('scroll', handleScroll)
        }
    }, [showModal])

    const handleAccept = (type: 'all' | 'necessary' | 'selected') => () => {
        switch (type) {
            case 'all':
                setStatistics(true)
                setMarketing(cookiesData.askMarketing)
                acceptCookies({ statistic: true, marketing: cookiesData.askMarketing })
                    .unwrap()
                    .then(() => {
                        const newData = {
                            askConsent: false,
                            marketingAllowed: cookiesData.askMarketing,
                            statisticAllowed: true,
                            askMarketing: cookiesData.askMarketing,
                        }
                        setCookiesDataToInput(newData)
                        setCookiesData(newData)
                    })
                break
            case 'necessary':
                setStatistics(false)
                setMarketing(false)
                acceptCookies({ statistic: false, marketing: false })
                    .unwrap()
                    .then(() => {
                        const newData = {
                            askConsent: false,
                            marketingAllowed: false,
                            statisticAllowed: false,
                            askMarketing: false,
                        }
                        setCookiesDataToInput(newData)
                        setCookiesData(newData)
                    })
                break
            case 'selected':
                acceptCookies({ statistic: statistics, marketing: cookiesData.askMarketing && marketing })
                    .unwrap()
                    .then(() => {
                        setCookiesDataToInput({
                            askConsent: false,
                            marketingAllowed: cookiesData.askMarketing && marketing,
                            statisticAllowed: statistics,
                            askMarketing: cookiesData.askMarketing,
                        })
                        setCookiesData({
                            askConsent: false,
                            marketingAllowed: cookiesData.askMarketing && marketing,
                            statisticAllowed: statistics,
                            askMarketing: cookiesData.askMarketing,
                        })
                    })
                break
            default:
                return
        }

        // localStorage.setItem('cookieAccepted', 'true')
        setShowModal(false)
    }

    useEffect(() => {
        setShowModal(cookiesData.askConsent)
    }, [location.pathname, cookiesData.askConsent])

    return (
        <Modal
            isOpen={showModal}
            onClose={() => undefined}
            closeOnEsc={false}
            closeOnOverlayClick={false}
            isCentered
            marginBottom="24px"
            trapFocus
            autoFocus={false}
            blockScrollOnMount
        >
            <ModalOverlay bg="rgba(15, 23, 42, 0.1)" />
            <ModalContent
                className={baseClass}
                aria-labelledby="cookie-consent-title"
                aria-describedby="cookie-consent-description"
                maxH="calc(100dvh - 24px)"
                w="calc(100vw - 24px)"
                maxW="800px"
                p={{ base: '16px', md: '24px' }}
                bg="white"
                borderRadius="8px"
                display="flex"
                flexWrap={'nowrap'}
                flexDirection="column"
                boxShadow={'0px 2px 10px rgba(0, 0, 0, 0.15)'}
                border={'1px solid #E5E5E5'}
                overflow="hidden"
                mb={{ base: '8px', md: '24px' }}
            >
                <Flex
                    sx={{
                        boxShadow: showTopShadow ? '0 11px 18px -15px rgba(0,0,0,0.3)' : '',
                        transition: 'box-shadow 0.2s ease',
                    }}
                    className={`${baseClass}__top`}
                    gap="16px"
                    borderBottom={'1px solid #EEEEEE'}
                    paddingBottom={'24px'}
                >
                    <IconCookie />
                    <Flex flexDirection={'column'} gap="8px">
                        <Text id="cookie-consent-title" color="#1A1A1A" fontWeight={600} fontSize="20px">
                            {t('7419')}
                        </Text>
                        <Text id="cookie-consent-description" fontSize={'14px'} lineHeight={'24px'}>
                            {t('7391')}{' '}
                            <Link
                                href={'/cookie-notice'}
                                target={'_blank'}
                                color={'#2F80ED'}
                                textTransform={'lowercase'}
                                fontWeight={'600'}
                            >
                                {t('4637')}
                            </Link>
                            .
                        </Text>
                    </Flex>
                </Flex>
                <Flex
                    ref={scrollRef}
                    direction={'column'}
                    flex="1"
                    minH={0}
                    overflowY="auto"
                    overscrollBehavior={'contain'}
                >
                    <CookieItem
                        title={t('9636', 'Necessary')}
                        description={t(
                            '9637',
                            'The necessary cookies are essential for the website to work properly and securely. We use three cookies that protect your information, maintain your login session, and ensure you have a smooth browsing experience. Without these three cookies, you would need to log in repeatedly, and many features would not function correctly. These cookies do not collect any personally identifiable information and are only used to provide you with a secure and convenient experience.'
                        )}
                        isChecked={necessary}
                        onChange={(e) => setNecessary(e.target.checked)}
                        isDisabled={true}
                    />
                    <CookieItem
                        title={t('3021', 'Statistics')}
                        description={t(
                            '9638',
                            'The statistics cookies help us improve our website by collecting anonymous information about your visit. We use these cookies to understand which pages are popular, how visitors navigate our site, and what content engages users most effectively. This data allows us to enhance site performance and create a better browsing experience for you. These cookies never identify you personally and only collect aggregated information for analysis purposes. Without statistics cookies, we would have limited insight into how to make our website more useful and user-friendly.'
                        )}
                        isChecked={statistics}
                        onChange={(e) => setStatistics(e.target.checked)}
                    />
                    {cookiesData.askMarketing && (
                        <CookieItem
                            title={t('4057', 'Marketing')}
                            description={t(
                                '9639',
                                "Marketing cookies allow us to show you personalized advertisements that match your interests both on our site and across the web. We use these cookies to understand your browsing patterns and preferences, helping us display content that's relevant to you. Without marketing cookies, we cannot tailor advertisements to your specific interests and preferences, which limits our ability to show you content that might be relevant or useful to you. These cookies track your browsing activity across different websites but never collect personally identifiable information like your name or email address. They simply help create a more engaging experience while allowing our advertising efforts to be more effective."
                            )}
                            isChecked={marketing}
                            onChange={(e) => setMarketing(e.target.checked)}
                        />
                    )}
                </Flex>
                <Grid
                    sx={{
                        boxShadow: showBottomShadow ? '0 -11px 18px -15px rgba(0,0,0,0.3)' : '',
                        transition: 'box-shadow 0.2s ease',
                    }}
                    borderTop={'1px solid #EEEEEE'}
                    paddingTop={{ base: '16px', md: '24px' }}
                    gap={{ base: '16px', md: '24px' }}
                    paddingLeft={{ base: '0', md: '62px' }}
                    templateColumns={{ base: '1fr', md: statistics || marketing ? 'auto auto auto' : 'auto auto' }}
                >
                    <AppButton translate="no" onClick={handleAccept('all')} variant={'primary'}>
                        {t('7254')}
                    </AppButton>
                    <AppButton
                        variant={'outline'}
                        onClick={handleAccept('necessary')}
                        sx={{
                            transition: 'background-color 0.2s ease',
                            _hover: {
                                backgroundColor: 'aliceblue',
                            },
                        }}
                    >
                        {t('9553')}
                    </AppButton>
                    {(statistics || marketing) && (
                        <AppButton
                            variant={'outline'}
                            onClick={handleAccept('selected')}
                            sx={{
                                transition: 'background-color 0.2s ease',
                                _hover: {
                                    backgroundColor: 'aliceblue',
                                },
                            }}
                        >
                            {t('9554')}
                        </AppButton>
                    )}
                </Grid>
            </ModalContent>
        </Modal>
    )
}

const CookieConsent: React.FC = () => {
    const [isMounted, setIsMounted] = useState(false)

    useEffect(() => {
        setIsMounted(true)
    }, [])

    if (!isMounted) return null

    return <CookieConsentModal />
}

export default CookieConsent

interface CookieItemProps {
    title?: null | string
    description?: null | string
    isChecked?: boolean
    isDisabled?: boolean
    onChange?: ((event: React.ChangeEvent<HTMLInputElement>) => void) | undefined
}

function CookieItem({
    title = '',
    description = '',
    isChecked = false,
    isDisabled = false,
    onChange,
}: CookieItemProps) {
    return (
        <Grid
            templateColumns={'max-content 1fr'}
            columnGap={'16px'}
            rowGap={'8px'}
            borderBottom={'1px solid #EEEEEE'}
            paddingBottom={{ base: '16px', md: '24px' }}
            paddingTop={{ base: '16px', md: '24px' }}
        >
            <GridItem>
                <Switch
                    size="lg"
                    disabled={isDisabled}
                    isChecked={isChecked}
                    onChange={onChange}
                    sx={{
                        transition: 'opacity 0.2s ease',
                        _hover: {
                            opacity: 0.7,
                        },
                        transform: 'scale(0.9)',
                    }}
                />
            </GridItem>
            <GridItem display={'flex'} fontWeight={600} color={'#1A1A1A'} alignItems={'center'}>
                {title}
            </GridItem>
            <GridItem></GridItem>
            <GridItem color={'#404040'} fontSize={'13px'} lineHeight={'18px'}>
                {description}
            </GridItem>
        </Grid>
    )
}

function extractCookiesData(): {
    askConsent: boolean
    marketingAllowed: boolean
    statisticAllowed: boolean
    askMarketing: boolean
} {
    const cookieInputs = Array.from(
        document.querySelectorAll<HTMLInputElement>('input[name="__cookie_settings"]')
    ).filter((input) => input.value)
    const defaultData = {
        askConsent: false,
        askMarketing: false,
        marketingAllowed: false,
        statisticAllowed: false,
    }

    const cookieInput = cookieInputs[cookieInputs.length - 1]
    if (!cookieInput) {
        return defaultData
    }
    try {
        return JSON.parse(cookieInput.value)
    } catch (error) {
        console.log(error)
        return defaultData
    }
}

function setCookiesDataToInput(data: {
    askConsent: boolean
    askMarketing: boolean
    marketingAllowed: boolean
    statisticAllowed: boolean
}): void {
    document.querySelectorAll<HTMLInputElement>('input[name="__cookie_settings"]').forEach((cookieInput) => {
        cookieInput.value = JSON.stringify(data)
    })
}
